// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import WidgetTitle from "@bg/App/Components/WidgetTitle";
import LazyImage, { LazyImageProps } from "@bg/MobiusOverrides/LazyImage";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import {
    getProductInfoFromList,
    getProductsForProductInfoList,
} from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import loadPurchasedProducts from "@insite/client-framework/Store/Components/PurchasedProducts/Handlers/LoadPurchasedProducts";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateSearchFields from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import Clickable from "@insite/mobius/Clickable";
import DataTable from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import Link, { LinkPresentationProps, LinkProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    viewAllTitle = "viewAllTitle",
    purchaseType = "purchaseType",
    showPrice = "showPrice",
    showAddToCart = "showAddToCart",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.viewAllTitle]: string;
        [fields.purchaseType]: string;
        [fields.showPrice]: boolean;
        [fields.showAddToCart]: boolean;
    };
}

interface ProductsLastPurchaseDate {
    productErpNumber: string;
    lastPurchasedDate: string;
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const result = getProductsForProductInfoList(state, props.id);
    const products: ProductContextModel[] | undefined = result?.map<ProductContextModel>(p => ({
        product: p,
        productInfo: getProductInfoFromList(state, props.id, p.id)!,
    }));
    const productDataView: any = getProductsDataView(state, {
        filter: "recentlyPurchased",
    });

    const properties =
        productDataView && productDataView.properties && productDataView.properties.productsLastPurchaseDate;
    let productsLastPurchaseDates: ProductsLastPurchaseDate[] = [];
    if (properties) {
        if (typeof properties === "string") {
            productsLastPurchaseDates = JSON.parse(properties);
        }
        if (properties instanceof Array) {
            productsLastPurchaseDates = properties;
        }
    }

    return {
        products,
        productsLastPurchaseDates,
        viewAllLink: getPageLinkByPageType(state, "OrderHistoryPage")?.url,
    };
};

const mapDispatchToProps = {
    loadPurchasedProducts,
    updateProductInfo,
    updateSearchFields,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps & HasHistory;

export interface RecentlyPurchasedProductsStyles {
    productDescription: DataTableCellProps;
    productDescriptionWrapper?: InjectableCss;
    productDescriptionLink?: LinkPresentationProps;
    lastPurchaseDate?: LinkPresentationProps;
    price?: DataTableCellProps;
    productQtyCell?: DataTableCellProps;
    productAddToCart?: DataTableCellProps;
    productQty?: TextFieldPresentationProps;
    centeredHeader?: DataTableHeaderProps;
    rightAlign?: InjectableCss;
    actionsCell?: InjectableCss;
    productPrice?: ProductPriceStyles;
    dataTableWrapper?: InjectableCss;
    atcWrapper?: InjectableCss;
}

export const styles: RecentlyPurchasedProductsStyles = {
    productDescription: {
        typographyProps: {
            ellipsis: true,
            color: "secondary",
        },
        css: css`
            max-width: 556px;
            font-size: ${fontConfig.fontSize.regular}px;
            font-weight: ${fontConfig.weight.semiBold};
            letter-spacing: 0.2px;
            line-height: 60px;
            padding: 0;
            padding-right: 20px;
        `,
    },
    productDescriptionWrapper: {
        css: css`
            display: flex;
            justify-content: flex-start;
            align-items: center;
        `,
    },
    productDescriptionLink: {
        typographyProps: {
            ellipsis: true,
        },
        css: css`
            max-width: 485px;
            margin-left: 20px;
        `,
    },
    lastPurchaseDate: {
        typographyProps: {
            color: "text.link",
            size: fontConfig.fontSize.regular,
            weight: fontConfig.weight.semiBold,
            css: css`
                letter-spacing: 0.2px;
                padding: 0;
            `,
        },
    },
    price: {
        css: css`
            padding: 0;
            text-align: right;
        `,
    },
    productPrice: {
        price: {
            priceText: {
                color: "text",
                size: fontConfig.fontSize.regular,
                weight: fontConfig.weight.regular,
                css: css`
                    letter-spacing: 0px;
                    padding: 0;
                `,
            },
            unitOfMeasureText: {
                color: "text",
                size: fontConfig.fontSize.regular,
                weight: fontConfig.weight.regular,
                transform: "uppercase",
                css: css`
                    letter-spacing: 0px;
                `,
            },
        },
    },
    productQtyCell: {
        css: css`
            &&& {
                width: 140px;
                margin-right: 10px;
            }
        `,
    },
    productQty: {
        cssOverrides: {
            formInputWrapper: css`
                &&&&&&& {
                    margin-top: 0;
                }
            `,
        },
    },
    productAddToCart: {
        css: css`
            &&& {
                > button {
                    width: 75px;
                }
                padding: 0;
            }
        `,
    },
    actionsCell: {
        css: css`
            > div {
                display: flex;
                justify-content: flex-end;
            }
            padding: 0px;
        `,
    },
    dataTableWrapper: {
        css: css`
            overflow-x: auto;
            position: relative;
            & th:first-child {
                position: relative !important;
            }
            & td:first-child {
                position: relative !important;
            }
        `,
    },
};

const RecentlyPurchasedProducts = ({
    fields,
    viewAllLink,
    products,
    loadPurchasedProducts,
    id,
    updateProductInfo,
    productsLastPurchaseDates,
    history,
    updateSearchFields,
}: Props) => {
    const [isLoading, setLoading] = useState(true);
    useEffect(() => {
        updateSearchFields({ productErpNumber: "" });
        loadPurchasedProducts({
            widgetId: id,
            purchaseType: fields.purchaseType,
            onComplete: () => {
                setLoading(false);
            },
        });
    }, []);

    if (isLoading) {
        return (
            <StyledWrapper
                css={css`
                    height: 100px;
                    width: 100%;
                    padding: 10px 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                `}
            >
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    if (!products || !products.length) {
        return null;
    }

    const handleUpdateSearchFields = (productErpNumber: string) => {
        updateSearchFields({ productErpNumber });

        if (viewAllLink) {
            history.push(viewAllLink);
        }
    };

    return (
        <>
            <WidgetTitle title={fields.title} viewAllText={fields.viewAllTitle} viewAllLink={viewAllLink} />
            <StyledWrapper {...styles.dataTableWrapper}>
                <DataTable>
                    <DataTableHead>
                        <DataTableHeader
                            tight
                            css={css`
                                padding-left: 10px;
                            `}
                            alignX="left"
                        >
                            {translate("Product Description")}
                        </DataTableHeader>
                        {fields.purchaseType === "recently" && (
                            <DataTableHeader
                                tight
                                css={css`
                                    padding: 0;
                                    min-width: 150px;
                                `}
                                alignX="left"
                            >
                                {translate("Last Purchased")}
                            </DataTableHeader>
                        )}
                        {fields.showPrice && (
                            <DataTableHeader
                                tight
                                css={css`
                                    padding: 0;
                                    text-align: right;
                                `}
                            >
                                {translate("Price")}
                            </DataTableHeader>
                        )}
                        <DataTableHeader
                            tight
                            css={css`
                                min-width: 270px;
                            `}
                        ></DataTableHeader>
                    </DataTableHead>
                    <DataTableBody>
                        {products.map(productContext => {
                            const { product, productInfo } = productContext;
                            const { lastPurchasedDate } = productsLastPurchaseDates.find(
                                d => d.productErpNumber === product.productNumber,
                            )!;

                            return (
                                <DataTableRow
                                    key={product.id}
                                    css={css`
                                        height: 77px;
                                    `}
                                >
                                    <DataTableCell {...styles.productDescription}>
                                        <StyledWrapper {...styles.productDescriptionWrapper}>
                                            <Clickable href={productInfo.productDetailPath}>
                                                <LazyImage
                                                    width="52px"
                                                    height="52px"
                                                    cloudinaryWidth="52"
                                                    src={product.smallImagePath}
                                                    altText={product.imageAltText || product.productTitle}
                                                    css={css`
                                                        min-width: 52px;
                                                    `}
                                                />
                                            </Clickable>

                                            <Link
                                                href={productInfo.productDetailPath}
                                                {...styles.productDescriptionLink}
                                            >
                                                {product.productTitle}
                                            </Link>
                                        </StyledWrapper>
                                    </DataTableCell>
                                    {fields.purchaseType === "recently" && (
                                        <DataTableCell
                                            css={css`
                                                padding: 0;
                                            `}
                                        >
                                            <Link
                                                onClick={() => handleUpdateSearchFields(product.productNumber)}
                                                {...styles.lastPurchaseDate}
                                            >
                                                {lastPurchasedDate}
                                            </Link>
                                        </DataTableCell>
                                    )}
                                    {fields.showPrice && product.canShowPrice && (
                                        <DataTableCell {...styles.price}>
                                            <ProductPrice
                                                product={productContext}
                                                showUnitOfMeasure={true}
                                                showLabel={false}
                                                showSavings={false}
                                                showSavingsAmount={false}
                                                showSavingsPercent={false}
                                                extendedStyles={styles.productPrice}
                                            />
                                        </DataTableCell>
                                    )}
                                    <ProductContext.Provider
                                        value={{
                                            product,
                                            productInfo,
                                            onQtyOrderedChanged: qtyOrdered =>
                                                updateProductInfo({ id, productId: product.id, qtyOrdered }),
                                        }}
                                    >
                                        {fields.showAddToCart && (
                                            <DataTableCell {...styles.actionsCell}>
                                                <StyledWrapper {...styles.atcWrapper}>
                                                    <StyledWrapper {...styles.productQtyCell}>
                                                        <ProductQuantityOrdered extendedStyles={styles.productQty} />
                                                    </StyledWrapper>

                                                    <StyledWrapper {...styles.productAddToCart}>
                                                        <ProductAddToCartButton
                                                            data-test-selector={`actionsAddToCart${product.id}`}
                                                        />
                                                    </StyledWrapper>
                                                </StyledWrapper>
                                            </DataTableCell>
                                        )}
                                    </ProductContext.Provider>
                                </DataTableRow>
                            );
                        })}
                    </DataTableBody>
                </DataTable>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(RecentlyPurchasedProducts)),
    definition: {
        group: "XNGAGE" as any,
        displayName: "Purchased Products",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Purchased Products",
                fieldType: "Translatable",
            },
            {
                name: fields.viewAllTitle,
                displayName: "View All Link Title",
                editorTemplate: "TextField",
                defaultValue: "View All",
                fieldType: "Translatable",
            },
            {
                name: fields.purchaseType,
                displayName: "Purchase Type",
                editorTemplate: "DropDownField",
                options: [
                    { value: "recently", displayName: "Recently Purchased" },
                    { value: "frequently", displayName: "Frequently Purchased" },
                ],
                defaultValue: "recently",
                fieldType: "General",
            },
            {
                name: fields.showPrice,
                displayName: "Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showAddToCart,
                displayName: "Add to Cart",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
